import { FC, useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import useComponentVisible from "hooks/useClickOutside";
interface MultiselectProps {
    options: any[];
    onSelect: (value: any[]) => void;
    value: any[];
    property?: string;
    searchable?: boolean;
    disabled?: boolean;
    label: string;
    onOpen?: Function;
    onClose?: (hasChanged?: boolean) => void;
    helperText?: string;
    error?: any;
    valueProperty?: string;
}

const MultiselectField: FC<MultiselectProps> = ({
    options,
    onSelect,
    value = [],
    property = 'title',
    searchable,
    label = '',
    disabled = false,
    helperText = '',
    onOpen,
    onClose,
    error,
    valueProperty = 'id'
}) => {
    const { isLoading } = useSelector((state: any) => state.sharedReducer);
    const [searchedOptions, setSearchedOptions] = useState<any[]>([]);
    const { ref, setIsComponentVisible, isComponentVisible } = useComponentVisible();
    const [search, setSearch] = useState<string>('');
    const searchRef = useRef(null);

    const toggleShowDropdown = () => {
        setIsComponentVisible(!isComponentVisible);
    }

    useEffect(() => {
        if (isComponentVisible) {
            if (searchable && searchRef && options.length) {
                (searchRef as any).current.focus();
            }
            if (onOpen) {
                onOpen();
            }
        } else {
            if (onClose) {
                onClose();
            }
        }
    }, [isComponentVisible])

    const handleSelect = (item: any) => {
        if (!disabled) {
            if (!item?.disabled) {
                if (value.includes(item[valueProperty])) {
                    const index = value.indexOf(item[valueProperty]);
                    value.splice(index, 1);
                } else {
                    value.push(item[valueProperty]);
                }
                onSelect(value);
            }
        }
    };


    const handleSearchChange = (event: any) => {
        const { value } = event.target;
        setSearch(value);
        const helper = options.filter((item) =>
            item[property].toLowerCase().includes(search.toLowerCase())
        );
        setSearchedOptions([...helper]);
    };

    const getOptions = (): any[] => {
        return search === '' ? options : searchedOptions;
    }

    const areAllSelected = (): boolean => {
        return options.filter(o => !o?.disabled).every(o => value.includes(o[valueProperty]));
    }

    const onSelectAll = () => {
        options.forEach(o => {
            // const { id } = o;
            if (!value.includes(o[valueProperty]) && !o?.disabled) {
                value.push(o[valueProperty]);
            }
        })

        onSelect(value);
    }

    const onDeSelectAll = () => {
        value = [];
        onSelect(value);
    }

    return (
        <div ref={ref} className="w-full flex flex-col">
            <div className="w-full relative" onClick={toggleShowDropdown}>
                <label className="text-label text-base font-semibold">{label}</label>
                <div className="w-full mt-1 flex items-center">
                    <div className="w-full h-[51px] flex items-center justify-between px-2 rounded-lg border-[0.6px] border-inputsBorderColor outline-none">
                        <input
                            type="text"
                            placeholder={helperText}
                            readOnly
                            className="outline-none "
                            />
                        {value.length > 0 && (
                            <div className="h-[25px] bg-blue-500 text-white text-xs whitespace-nowrap font-medium px-2 py-1 rounded">
                                {value.length} selected
                            </div>
                        )}
                    </div>
                </div>
            </div>

            {isComponentVisible && (
                <div className="relative w-full">
                    <ul className="absolute w-full max-h-56 overflow-y-auto bg-white shadow-lg rounded-md p-2 border border-gray-300 z-50">
                        {searchable && options.length > 0 && (
                            <div className="w-full p-2 bg-gray-100 rounded-md mb-2">
                                <input
                                    type="search"
                                    placeholder="Search"
                                    onChange={handleSearchChange}
                                    ref={searchRef}
                                    className="w-full p-2 bg-white border border-gray-300 rounded-md outline-none"
                                />
                            </div>
                        )}

                        {options.length > 0 && !disabled && (
                            <li
                                className="flex items-center gap-2 p-2 cursor-pointer hover:bg-gray-200 rounded-md"
                                onClick={areAllSelected() ? onDeSelectAll : onSelectAll}
                            >
                                <input
                                    type="checkbox"
                                    checked={areAllSelected()}
                                    readOnly
                                    className="h-4 w-4 text-blue-500 cursor-pointer"
                                />
                                <span className="text-sm text-gray-700">
                                    {areAllSelected() ? "Deselect All" : "Select All"}
                                </span>
                            </li>
                        )}

                        {getOptions().map((item: any, index: number) => (
                            <li
                                key={index}
                                onClick={() => handleSelect(item)}
                                className={`flex items-center gap-2 p-2 cursor-pointer hover:bg-gray-200 rounded-md ${item?.disabled || disabled ? "opacity-50 cursor-not-allowed" : ""
                                    }`}
                            >
                                <input
                                    type="checkbox"
                                    checked={value.includes(item[valueProperty])}
                                    readOnly
                                    disabled={disabled}
                                    className="h-4 w-4 text-blue-500 cursor-pointer"
                                />
                                <span className="text-sm text-gray-700">{item?.[property]}</span>
                            </li>
                        ))}

                        {!getOptions().length && !isLoading && (
                            <span className="block text-center text-sm text-gray-500 py-2">No options found</span>
                        )}
                    </ul>
                </div>
            )}

            {error && !isComponentVisible && (
                <span className="text-red-500 text-sm mt-1">{error}</span>
            )}
        </div>

    );
};

export default MultiselectField;
